// TypeScript interfaces and constants for MediaUrlProcessor

// Re-export external types
export type { ImageUrlData } from '../../../../Creation/Image/ImageUploadHelper';
export type { DocumentUrlData } from '../../../../Creation/Document/DocumentUploadHelper';

// Define constants for supported file formats
export const SUPPORTED_IMAGE_FORMATS = [ 'jpg', 'jpeg', 'png', 'gif', 'webp' ];
export const SUPPORTED_DOCUMENT_FORMATS = [ 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf' ];
export const SUPPORTED_PODCAST_FORMATS = [ 'mp3' ]; // Currently only MP3 for podcasts

// Maximum data URI size (5MB)
export const MAX_DATA_URI_SIZE = 5 * 1024 * 1024;

// Error messages
export const ERROR_MESSAGES = {
	INVALID_URL          : 'Invalid URL format',
	INVALID_FILE_TYPE    : 'Invalid URL format. Please check the file extension.',
	INVALID_YOUTUBE_URL  : 'Invalid YouTube URL format',
	INVALID_IMAGE_URL    : 'URL must point to a supported image format (JPG, PNG, GIF, WebP)',
	INVALID_DOCUMENT_URL : 'URL must point to a supported document format (PDF, DOC, XLS, PPT, etc.)',
	INVALID_PODCAST_URL  : 'URL must point to a supported audio format (MP3)',
	MULTIPLE_URLS        : 'Please enter only one URL at a time',
	IN_PROGRESS          : 'Another upload is in progress, please wait or try again later'
} as const;

export type ErrorMessageKeys = keyof typeof ERROR_MESSAGES;

// Podcast URL data interface
export interface PodcastUrlData {
	url: string;
	title?: string;
	description?: string;
	fileType: string;
	platform?: string;
	metadata?: {
		isDataUri?: boolean;
		sizeInBytes?: number;
		duration?: number;
		fileSize?: number;
	};
}

// Core upload result interface
export interface UploadResult {
	url: string;
	source: string;
	key?: string;
	videoId?: string;
	thumbnail?: string;
	fileType?: string;
	title?: string;
	isEmbedded?: boolean;
	platform?: string;
	readyForUpload?: boolean;
	// Additional metadata for data URIs
	metadata?: {
		isDataUri: boolean;
		sizeInBytes?: number;
		dimensions?: {
			width: number;
			height: number;
		};
		pageCount?: number;
		duration?: number;
		fileSize?: number;
	};
	skipParentCallback?: boolean;
}

// Main component props
export interface MediaUrlProcessorProps {
	Embednote: string;
	fileType: 'video' | 'image' | 'document' | 'podcast';
	onFileSelect?: (url: string, source: string, key?: string) => void;
	onClose?: () => void;
	companyId?: string;
	fromCreation?: string;
}

// Child component props
export interface EmbedVideosProps {
	url: string;
	isProcessing: boolean;
	onUploadComplete: (result: UploadResult) => void;
	onError: (error: string) => void;
	companyId: string;
}

export interface EmbedImagesProps {
	url: string;
	isProcessing: boolean;
	imagePreview: string | null;
	setImagePreview: (preview: string | null) => void;
	onUploadComplete: (result: UploadResult) => void;
	onError: (error: string) => void;
	companyId: string;
	fromCreation?: string;
}

export interface EmbedDocumentsProps {
	url: string;
	isProcessing: boolean;
	onUploadComplete: (result: UploadResult) => void;
	onError: (error: string) => void;
	companyId: string;
}

export interface EmbedPodcastsProps {
	url: string;
	isProcessing: boolean;
	onUploadComplete: (result: UploadResult) => void;
	onError: (error: string) => void;
	companyId: string;
}

// YouTube video data interface
export interface YouTubeVideoData {
	videoUrl: string;
	videoId: string;
	thumbnailUrl: string;
	title?: string;
	description?: string;
}

// Image validation result
export interface ImageValidationResult {
	valid: boolean;
	reason?: string;
}

// Image dimensions interface
export interface ImageDimensions {
	width: number;
	height: number;
}